import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Wallet, RefreshCw, Star, ChevronRight, AlertTriangle, Lock, Printer, FileSpreadsheet } from 'lucide-react';
import { exportToCSV, printTable } from '../../lib/printUtils';

const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');

const PCT_BAR_COLORS = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  gray: 'bg-gray-400',
};
const PCT_TEXT_COLORS = {
  red: 'text-red-700',
  amber: 'text-amber-700',
  emerald: 'text-emerald-700',
  gray: 'text-gray-700',
};

export default function CajasRendirReportePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/cajas-rendir/reporte');
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando reporte');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Carlos (09/05/2026): exportar/imprimir reporte de cajas
  const cajasColumns = [
    { key: 'codigo', label: 'Código', width: '50px' },
    { key: 'etiqueta', label: 'Caja' },
    { key: 'trabajador_nombre', label: 'Responsable', format: (v, r) => v || r.usuario_email || '-' },
    { key: 'estado', label: 'Estado', align: 'center', format: (v) => (v || 'activa').toUpperCase() },
    { key: 'monto_asignado', label: 'Asignado', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
    { key: 'consumido_total', label: 'Consumido', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
    { key: 'consumido_count', label: '#OCs', align: 'center' },
    { key: 'saldo_calculado', label: 'Saldo', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
    { key: 'pct_consumido', label: '% Uso', align: 'center', format: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
    { key: 'mes_actual_monto', label: 'Mes actual', align: 'right', mono: true, format: (v, r) => `${r.mes_actual_count}·$${fmt(v)}` },
  ];

  const buildSummary = () => data ? [
    { label: 'Cajas activas', value: data.totales.cajas_activas },
    { label: 'Total asignado', value: `$${fmt(data.totales.total_asignado)}`, color: '#b45309' },
    { label: 'Total consumido', value: `$${fmt(data.totales.total_consumido)}`, color: '#b91c1c' },
    { label: 'Saldo global', value: `$${fmt(data.totales.saldo_global)}`, color: '#047857', highlight: true },
  ] : [];

  const imprimirReporte = () => {
    if (!data) return;
    printTable({
      title: 'Reporte de Cajas a Rendir',
      subtitle: `${data.cajas.length} cajas · Generado ${new Date().toLocaleString('es-CL')}`,
      summary: buildSummary(),
      columns: cajasColumns,
      rows: data.cajas,
    });
  };

  const exportarExcel = () => {
    if (!data) return;
    exportToCSV({
      filename: 'reporte_cajas_a_rendir',
      title: 'CHIRINO STEEL · Reporte de Cajas a Rendir',
      summary: [
        { label: 'Cajas activas', value: data.totales.cajas_activas },
        { label: 'Total asignado (CLP)', value: Math.round(data.totales.total_asignado) },
        { label: 'Total consumido (CLP)', value: Math.round(data.totales.total_consumido) },
        { label: 'Saldo global (CLP)', value: Math.round(data.totales.saldo_global) },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'codigo', label: 'Código' },
        { key: 'etiqueta', label: 'Caja' },
        { key: 'trabajador_nombre', label: 'Responsable', format: (v, r) => v || r.usuario_email || '' },
        { key: 'usuario_email', label: 'Email' },
        { key: 'estado', label: 'Estado', format: (v) => (v || 'activa').toUpperCase() },
        { key: 'monto_asignado', label: 'Asignado (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'consumido_total', label: 'Consumido (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'consumido_count', label: 'Cant. OCs' },
        { key: 'saldo_calculado', label: 'Saldo (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'pct_consumido', label: '% Uso', format: (v) => v != null ? `${v.toFixed(1)}%` : '' },
        { key: 'mes_actual_count', label: 'OCs mes actual' },
        { key: 'mes_actual_monto', label: 'Monto mes (CLP)', format: (v) => Math.round(Number(v) || 0) },
      ],
      rows: data.cajas,
    });
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="cajas-reporte-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-6 h-6 text-amber-600" />
            Reporte de Cajas a Rendir
          </h1>
          <p className="text-sm text-gray-500 mt-1">Consumo de OCs por caja y saldo restante.</p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <>
              <button
                onClick={imprimirReporte}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-700 rounded text-sm hover:bg-blue-50"
                title="Imprimir reporte"
                data-testid="btn-print-reporte-cajas"
              >
                <Printer className="w-4 h-4" /> Imprimir
              </button>
              <button
                onClick={exportarExcel}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded text-sm hover:bg-emerald-50"
                title="Exportar a Excel/CSV"
                data-testid="btn-excel-reporte-cajas"
              >
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </button>
            </>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 disabled:opacity-50"
            data-testid="btn-refresh-cajas"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
            <KPI label="Cajas activas" value={data.totales.cajas_activas} color="indigo" testId="kpi-cajas-activas" />
            <KPI label="Total asignado" value={`$${fmt(data.totales.total_asignado)}`} color="amber" testId="kpi-asignado" />
            <KPI label="Total consumido" value={`$${fmt(data.totales.total_consumido)}`} color="red" testId="kpi-consumido" />
            <KPI label="Saldo global" value={`$${fmt(data.totales.saldo_global)}`} color="emerald" testId="kpi-saldo" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.cajas.map((c) => (
              <CajaCard
                key={c.codigo}
                c={c}
                isExpanded={expanded === c.codigo}
                onToggle={() => setExpanded(expanded === c.codigo ? null : c.codigo)}
              />
            ))}
            {data.cajas.length === 0 && (
              <div className="col-span-full p-8 text-center text-gray-500 border rounded">
                No hay cajas configuradas. Crea una en <code className="bg-gray-100 px-1 rounded">/admin/cajas-rendir</code>.
              </div>
            )}
          </div>

          <div className="text-xs text-gray-400 mt-6 text-right">
            Última actualización: {(data.checked_at || '').replace('T', ' ').slice(0, 19)} UTC
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, color, testId }) {
  const colors = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };
  return (
    <div className={`p-4 rounded-lg border ${colors[color]}`} data-testid={testId}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-gray-600 mt-1">{label}</div>
    </div>
  );
}

function CajaCard({ c, isExpanded, onToggle }) {
  const pct = c.pct_consumido;
  const pctColor =
    pct == null ? 'gray' :
    pct >= 100 ? 'red' :
    pct >= 80 ? 'amber' : 'emerald';

  const isCerrada = c.estado === 'cerrada';
  const isOverbudget = (c.saldo_calculado || 0) < 0;

  return (
    <div
      className={`border rounded-lg overflow-hidden ${
        isCerrada ? 'bg-gray-50 opacity-80' : (isOverbudget ? 'border-red-300 shadow-sm' : 'border-amber-200')
      }`}
      data-testid={`card-caja-${c.codigo}`}
    >
      <div className={`px-4 py-3 ${isCerrada ? 'bg-gray-100' : 'bg-amber-50/70'} border-b`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {c.favorita && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />}
              <Wallet className="w-3.5 h-3.5 text-amber-700" />
              <span className="font-semibold text-sm truncate">{c.etiqueta}</span>
              <span className="text-[10px] uppercase tracking-wide bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{c.codigo}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">
              {c.trabajador_nombre || c.usuario_email || 'Sin responsable'}
              {c.usuario_email && c.trabajador_nombre && <span> · {c.usuario_email}</span>}
            </div>
          </div>
          {isCerrada && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-700 whitespace-nowrap flex items-center gap-1">
              <Lock className="w-3 h-3" /> Cerrada
            </span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Asignado</div>
            <div className="font-bold">${fmt(c.monto_asignado)}</div>
          </div>
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Consumido total</div>
            <div className="font-bold">${fmt(c.consumido_total)}</div>
            <div className="text-xs text-gray-500">{c.consumido_count} OCs</div>
          </div>
        </div>

        {c.monto_asignado > 0 && (
          <div>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-gray-500">Uso del monto asignado</span>
              <span className={`font-semibold ${PCT_TEXT_COLORS[pctColor]}`}>{pct?.toFixed(1) || '0'}%</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${PCT_BAR_COLORS[pctColor]}`}
                style={{ width: `${Math.min(100, pct || 0)}%` }}
              />
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-gray-100">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">Saldo calculado:</span>
            <span className={`font-bold text-base ${isOverbudget ? 'text-red-700' : 'text-emerald-700'}`}>
              {isOverbudget && <AlertTriangle className="w-4 h-4 inline mr-1" />}
              ${fmt(c.saldo_calculado)}
            </span>
          </div>
          {isOverbudget && (
            <div className="text-[10px] text-red-600 mt-1">⚠️ Caja excedida en ${fmt(Math.abs(c.saldo_calculado))}</div>
          )}
        </div>

        <div className="pt-2 border-t border-gray-100 text-xs">
          <span className="text-gray-500">Mes actual:</span>{' '}
          <span className="font-medium">{c.mes_actual_count} OCs · ${fmt(c.mes_actual_monto)}</span>
        </div>

        {c.ocs_recientes?.length > 0 && (
          <>
            <button
              onClick={onToggle}
              className="text-xs text-amber-700 hover:underline flex items-center gap-1 font-medium"
              data-testid={`btn-toggle-${c.codigo}`}
            >
              <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              {isExpanded ? 'Ocultar' : 'Ver'} últimas {c.ocs_recientes.length} OCs
            </button>
            {isExpanded && (
              <div className="space-y-1 pt-2 border-t border-gray-100" data-testid={`recientes-${c.codigo}`}>
                {c.ocs_recientes.map((oc, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                    <div className="min-w-0 flex-1">
                      <span className="font-mono font-semibold">OC {oc.numero_oc}</span>
                      <span className="text-gray-500 ml-2 truncate">{oc.razon_social_proveedor || oc.rut_proveedor}</span>
                      {oc.creado_por && <div className="text-[10px] text-gray-400">por: {oc.creado_por}</div>}
                    </div>
                    <div className="text-right whitespace-nowrap ml-2">
                      <div className="font-semibold">${fmt(oc.neto)}</div>
                      <div className="text-[10px] text-gray-400">{(oc.fecha_creacion || '').slice(0, 10)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
